import configuration from '~/configuration';
import { readConsentChoice } from '~/core/consent/choice';
import { isBrowser } from '~/core/generic/is-browser';
import logger, { setSentryModule } from '~/core/logger';

let initialized = false;
let initialization: Promise<boolean> | null = null;
let generation = 0;
let sentryModule: typeof import('@sentry/react') | null = null;
let sentryClient: ReturnType<typeof import('@sentry/react').getClient>;
let replayIntegration: InstanceType<
  typeof import('@sentry/replay').Replay
> | null = null;
let stopping: Promise<void> | null = null;
let activeDiagnosticsGrantId: string | null = null;
const replayIdsForActiveGrant = new Set<string>();
const revokedReplayIds = new Set<string>();
const capturedEventIds = new Set<string>();
const acceptedEventIds = new Set<string>();
const acceptedErrorIds = new Set<string>();
const MAX_PENDING_CAPTURE_IDS = 4_096;
const MAX_PENDING_AFTER_SEND_IDS = 4_096;
const MAX_REPLAY_ERROR_IDS = 512;
const TRANSACTION_GRANT_ID_METADATA_KEY = 'directordeckDiagnosticsGrantId';

type BrowserSentryClient = NonNullable<
  ReturnType<typeof import('@sentry/react').getClient>
>;

function rememberReplayId(replayId: string | undefined) {
  if (replayId) replayIdsForActiveGrant.add(replayId);
}

function hasActiveDiagnosticsGrant(): boolean {
  const choice = readConsentChoice();
  return (
    activeDiagnosticsGrantId !== null &&
    choice?.diagnostics === true &&
    choice.diagnosticsGrantId === activeDiagnosticsGrantId
  );
}

function sampleTracesWhileAllowed(): number {
  return hasActiveDiagnosticsGrant() ? 1 : 0;
}

function newSentryEventId(): string {
  try {
    const randomUUID = globalThis.crypto?.randomUUID?.();
    if (randomUUID) return randomUUID.replace(/-/g, '');
    if (globalThis.crypto?.getRandomValues) {
      const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
      return Array.from(bytes, (byte) =>
        byte.toString(16).padStart(2, '0'),
      ).join('');
    }
  } catch {
    // Match the pinned SDK's fallback when browser crypto is unavailable.
  }
  return Array.from({ length: 32 }, () =>
    Math.floor(Math.random() * 16).toString(16),
  ).join('');
}

function rememberCapturedEventId(eventId: string) {
  capturedEventIds.add(eventId);
  // A processor can discard an event before beforeSend runs. Bound those
  // otherwise invisible pending captures in long-lived tabs.
  while (capturedEventIds.size > MAX_PENDING_CAPTURE_IDS) {
    const oldest = capturedEventIds.values().next().value;
    if (!oldest) break;
    capturedEventIds.delete(oldest);
  }
}

function rememberAcceptedErrorId(eventId: string) {
  acceptedErrorIds.add(eventId);
  if (acceptedErrorIds.size <= MAX_REPLAY_ERROR_IDS) return;
  // The pinned Replay SDK retains at most 100 linked errors in its current
  // context. Preserve those while retiring older IDs that Replay did not link.
  const activeReplayErrorIds = (
    replayIntegration as unknown as {
      _replay?: { getContext?: () => { errorIds?: Set<string> } };
    } | null
  )?._replay?.getContext?.().errorIds;
  for (const id of acceptedErrorIds) {
    if (acceptedErrorIds.size <= MAX_REPLAY_ERROR_IDS) break;
    if (!activeReplayErrorIds?.has(id)) acceptedErrorIds.delete(id);
  }
}

function rememberAcceptedEventId(eventId: string) {
  acceptedEventIds.add(eventId);
  while (acceptedEventIds.size > MAX_PENDING_AFTER_SEND_IDS) {
    const oldest = acceptedEventIds.values().next().value;
    if (!oldest) break;
    acceptedEventIds.delete(oldest);
  }
}

function installCaptureFence(client: BrowserSentryClient): boolean {
  if (
    typeof client.captureException !== 'function' ||
    typeof client.captureMessage !== 'function' ||
    typeof client.captureEvent !== 'function'
  )
    return false;

  const captureException = client.captureException.bind(client);
  const captureMessage = client.captureMessage.bind(client);
  const captureEvent = client.captureEvent.bind(client);
  client.captureException = (exception, hint, scope) => {
    if (!hasActiveDiagnosticsGrant()) return undefined;
    const eventId = hint?.event_id ?? newSentryEventId();
    rememberCapturedEventId(eventId);
    const result = captureException(
      exception,
      { ...hint, event_id: eventId },
      scope,
    );
    if (!result) capturedEventIds.delete(eventId);
    return result;
  };
  client.captureMessage = (message, level, hint, scope) => {
    if (!hasActiveDiagnosticsGrant()) return undefined;
    const eventId = hint?.event_id ?? newSentryEventId();
    rememberCapturedEventId(eventId);
    const result = captureMessage(
      message,
      level,
      { ...hint, event_id: eventId },
      scope,
    );
    if (!result) capturedEventIds.delete(eventId);
    return result;
  };
  client.captureEvent = (event, hint, scope) => {
    if (!hasActiveDiagnosticsGrant()) return undefined;
    if (event.type !== 'transaction') {
      const eventId = event.event_id ?? hint?.event_id ?? newSentryEventId();
      rememberCapturedEventId(eventId);
      const result = captureEvent(event, { ...hint, event_id: eventId }, scope);
      if (!result) capturedEventIds.delete(eventId);
      return result;
    }
    return captureEvent(event, hint, scope);
  };
  return true;
}

function installAfterSendFence(client: BrowserSentryClient): boolean {
  if (typeof client.emit !== 'function') return false;
  const emit = client.emit.bind(client) as (
    hook: string,
    ...args: unknown[]
  ) => void;
  client.emit = ((hook, ...args) => {
    if (hook === 'afterSendEvent') {
      const event = args[0] as
        | { event_id?: string; tags?: { replayId?: string } }
        | undefined;
      const eventId = event?.event_id;
      if (
        !hasActiveDiagnosticsGrant() ||
        !eventId ||
        !acceptedEventIds.has(eventId)
      )
        return;
      // Replay can renew its session without calling startBuffering or
      // _initialize. Register only the SDK's current ID while this grant is
      // still active; a callback from a revoked grant stays rejected above.
      rememberReplayId(replayIntegration?.getReplayId());
      if (
        event.tags?.replayId &&
        !replayIdsForActiveGrant.has(event.tags.replayId)
      )
        return;
      acceptedEventIds.delete(eventId);
    }
    emit(hook, ...args);
  }) as typeof client.emit;
  return true;
}

/** Load browser error monitoring only after an explicit diagnostics opt-in. */
export function initializeBrowserSentry(): Promise<boolean> {
  if (!isBrowser()) return Promise.resolve(false);
  const startingChoice = readConsentChoice();
  const startingGrantId = startingChoice?.diagnosticsGrantId;
  if (startingChoice?.diagnostics !== true || !startingGrantId)
    return Promise.resolve(false);
  if (initialized) return Promise.resolve(hasActiveDiagnosticsGrant());
  if (initialization) return initialization;
  if (!configuration.sentry.clientEnabled) return Promise.resolve(false);

  const dsn = configuration.sentry.clientDsn;
  if (!dsn) {
    warnSentryNotConfigured();
    return Promise.resolve(false);
  }

  const startingGeneration = generation;
  const stillAllowed = () =>
    startingGeneration === generation &&
    readConsentChoice()?.diagnosticsGrantId === startingGrantId;

  const pending = (async () => {
    if (stopping) await stopping;
    if (!stillAllowed()) return false;

    const { BrowserTracing } = await import('@sentry/browser');
    if (!stillAllowed()) return false;

    const { Replay } = await import('@sentry/replay');
    if (!stillAllowed()) return false;

    const Sentry = await import('@sentry/react');
    if (!stillAllowed()) return false;

    if (replayIntegration) {
      if (!sentryClient) return false;
      // The pinned SDK installs Replay error hooks on the first client only.
      // Reuse that client so error-triggered Replay still works after opt-in.
      // Close any browser session created during denial while the client is
      // still disabled, so its identifier cannot be sent on the next route.
      Sentry.startSession({ ignoreDuration: true });
      activeDiagnosticsGrantId = startingGrantId;
      const options = sentryClient.getOptions();
      options.tracesSampler = sampleTracesWhileAllowed;
      options.enabled = true;
      try {
        replayIntegration.startBuffering();
      } catch (error) {
        activeDiagnosticsGrantId = null;
        options.enabled = false;
        delete options.tracesSampler;
        throw error;
      }
      sentryModule = Sentry;
      setSentryModule(Sentry);
      initialized = true;
      Sentry.captureSession();
      return true;
    }

    const replay = new (class extends Replay {
      private restarted = false;

      override startBuffering() {
        this.restarted = true;
        super.startBuffering();
        rememberReplayId(this.getReplayId());
      }

      protected override _initialize() {
        // setupOnce schedules this callback. A same-turn withdrawal must
        // prevent the delayed callback from starting a new recording.
        if (hasActiveDiagnosticsGrant() && !this.restarted) {
          super._initialize();
          rememberReplayId(this.getReplayId());
        }
      }
    })();
    replayIntegration = replay;
    // BrowserTracing starts its first pageload transaction during Sentry.init.
    activeDiagnosticsGrantId = startingGrantId;

    Sentry.init({
      dsn,
      integrations: [
        new BrowserTracing({
          // The pinned SDK keeps request instrumentation installed after
          // withdrawal and cannot gate propagation per request. Keep browser
          // transactions, but never attach Sentry IDs to application traffic.
          tracePropagationTargets: [],
          beforeNavigate(context) {
            const grantId = hasActiveDiagnosticsGrant()
              ? activeDiagnosticsGrantId
              : null;
            return {
              ...context,
              ...(!grantId && { sampled: false }),
              metadata: {
                ...context.metadata,
                [TRANSACTION_GRANT_ID_METADATA_KEY]: grantId,
              },
            };
          },
        }),
        replay,
        // The structured browser logger calls captureException directly.
      ],
      tracesSampler: sampleTracesWhileAllowed,
      replaysSessionSampleRate: 0.0,
      replaysOnErrorSampleRate: 1.0,
      environment: configuration.sentry.environment,
      release: configuration.sentry.release,
      sendDefaultPii: true,
      beforeBreadcrumb(breadcrumb) {
        return hasActiveDiagnosticsGrant() ? breadcrumb : null;
      },
      beforeSendTransaction(event) {
        const grantId = (
          event.sdkProcessingMetadata as
            | (typeof event.sdkProcessingMetadata & {
                [TRANSACTION_GRANT_ID_METADATA_KEY]?: string;
              })
            | undefined
        )?.[TRANSACTION_GRANT_ID_METADATA_KEY];
        return hasActiveDiagnosticsGrant() &&
          grantId === activeDiagnosticsGrantId
          ? event
          : null;
      },
      beforeSend(event, hint) {
        const reject = () => {
          if (event.event_id) capturedEventIds.delete(event.event_id);
          return null;
        };
        if (
          !hasActiveDiagnosticsGrant() ||
          !event.event_id ||
          !capturedEventIds.has(event.event_id)
        )
          return reject();

        const error = hint.originalException;
        if (error instanceof Error) {
          if (error.message?.includes('popup-closed-by-user')) return reject();
          if (error.message?.includes('cancelled-popup-request'))
            return reject();
          if (error.message?.includes('Loading initial props cancelled'))
            return reject();
        }

        const message = event.message || '';
        if (
          message.includes('WebChannelConnection') &&
          message.includes('transport errored')
        ) {
          return reject();
        }

        return event;
      },
    });

    sentryClient = Sentry.getClient();
    activeDiagnosticsGrantId = startingGrantId;
    if (
      !sentryClient ||
      !installCaptureFence(sentryClient) ||
      !installAfterSendFence(sentryClient)
    ) {
      await stopBrowserSentry();
      return false;
    }
    sentryClient?.addEventProcessor?.((event) => {
      if (event.type === 'replay_event') {
        if (!hasActiveDiagnosticsGrant()) return null;
        const replayId = (event as { replay_id?: string }).replay_id;
        const currentReplayId = replayIntegration?.getReplayId();
        if (
          !replayId ||
          revokedReplayIds.has(replayId) ||
          (currentReplayId &&
            replayId !== currentReplayId &&
            !replayIdsForActiveGrant.has(replayId))
        )
          return null;
        rememberReplayId(replayId);
      }
      return event;
    });
    const transport = sentryClient?.getTransport?.();
    if (transport) {
      const baseTransportMarker = (
        transport.send as typeof transport.send & {
          __sentry__baseTransport__?: boolean;
        }
      ).__sentry__baseTransport__;
      const send = transport.send.bind(transport);
      const guardedSend: typeof transport.send = (envelope) => {
        if (!hasActiveDiagnosticsGrant())
          return Promise.resolve({ statusCode: 200 });
        const outgoingEvents: Array<{ id: string; type: string }> = [];
        for (const [headers, payload] of envelope[1]) {
          if (headers.type !== 'event' && headers.type !== 'transaction')
            continue;
          const eventId =
            payload && typeof payload === 'object' && 'event_id' in payload
              ? payload.event_id
              : undefined;
          if (
            headers.type === 'event' &&
            (typeof eventId !== 'string' || !capturedEventIds.has(eventId))
          )
            return Promise.resolve({ statusCode: 200 });
          if (typeof eventId === 'string')
            outgoingEvents.push({ id: eventId, type: headers.type });
        }
        const replayEvent = envelope[1].find(
          ([headers]) => headers.type === 'replay_event',
        );
        const replayPayload = replayEvent?.[1];
        const replayId =
          replayPayload &&
          typeof replayPayload === 'object' &&
          'replay_id' in replayPayload
            ? replayPayload.replay_id
            : undefined;
        const referencedErrorIds =
          replayPayload &&
          typeof replayPayload === 'object' &&
          'error_ids' in replayPayload
            ? replayPayload.error_ids
            : undefined;
        if (
          replayEvent &&
          (typeof replayId !== 'string' ||
            revokedReplayIds.has(replayId) ||
            (referencedErrorIds !== undefined &&
              (!Array.isArray(referencedErrorIds) ||
                referencedErrorIds.some(
                  (id) => typeof id !== 'string' || !acceptedErrorIds.has(id),
                ))))
        ) {
          return Promise.resolve({ statusCode: 200 });
        }
        const sendingGeneration = generation;
        const sendingGrantId = activeDiagnosticsGrantId;
        const replayErrorIds = Array.isArray(referencedErrorIds)
          ? (referencedErrorIds as string[])
          : [];
        return send(envelope).then(
          (response) => {
            if (generation === sendingGeneration)
              for (const event of outgoingEvents)
                if (event.type === 'event') capturedEventIds.delete(event.id);
            if (
              generation === sendingGeneration &&
              activeDiagnosticsGrantId === sendingGrantId &&
              hasActiveDiagnosticsGrant() &&
              response &&
              typeof response.statusCode === 'number' &&
              response.statusCode >= 200 &&
              response.statusCode < 300
            ) {
              for (const event of outgoingEvents) {
                rememberAcceptedEventId(event.id);
                if (event.type === 'event') rememberAcceptedErrorId(event.id);
              }
              for (const id of replayErrorIds) acceptedErrorIds.delete(id);
            }
            return response;
          },
          (error) => {
            if (generation === sendingGeneration)
              for (const event of outgoingEvents)
                if (event.type === 'event') capturedEventIds.delete(event.id);
            throw error;
          },
        );
      };
      transport.send = Object.assign(guardedSend, {
        __sentry__baseTransport__: baseTransportMarker,
      });
    }
    sentryModule = Sentry;
    setSentryModule(Sentry);
    if (!stillAllowed()) {
      await stopBrowserSentry();
      return false;
    }
    initialized = true;
    return true;
  })();

  initialization = pending;
  void pending.then(
    () => {
      if (initialization === pending) initialization = null;
    },
    () => {
      if (initialization === pending) initialization = null;
    },
  );
  return pending;
}

export function setBrowserSentryUser(
  user: Parameters<typeof import('@sentry/react').setUser>[0],
) {
  if (!initialized || !hasActiveDiagnosticsGrant()) return;
  sentryModule?.setUser(user);
}

/** Discard Replay and disable its client when diagnostics consent is withdrawn. */
export function stopBrowserSentry(): Promise<void> {
  generation += 1;
  initialization = null;
  initialized = false;
  rememberReplayId(replayIntegration?.getReplayId());
  for (const replayId of replayIdsForActiveGrant)
    revokedReplayIds.add(replayId);
  replayIdsForActiveGrant.clear();
  capturedEventIds.clear();
  acceptedEventIds.clear();
  acceptedErrorIds.clear();
  activeDiagnosticsGrantId = null;

  const sdk = sentryModule;
  sentryModule = null;
  setSentryModule(null);
  if (sentryClient) {
    const options = sentryClient.getOptions();
    options.enabled = false;
    // In Sentry 7, outgoing request instrumentation checks for this option's
    // presence even when enabled=false or the sampler returns zero.
    delete options.tracesSampler;
  }
  // Ending an old route transaction while disabled prevents its spans from
  // collecting activity during the denied interval or finishing after opt-in.
  sdk?.getActiveTransaction()?.end();
  sdk?.getCurrentScope().clearBreadcrumbs();
  sdk?.setUser(null);
  if (stopping) return stopping;

  const pending = (async () => {
    if (replayIntegration) await discardReplayBuffer(replayIntegration);
  })();
  stopping = pending;
  void pending.then(
    () => {
      if (stopping === pending) stopping = null;
    },
    () => {
      if (stopping === pending) stopping = null;
    },
  );
  return pending;
}

async function discardReplayBuffer(
  replay: InstanceType<typeof import('@sentry/replay').Replay>,
): Promise<void> {
  // Pinned @sentry/replay 7.120.4: Replay.stop() force-flushes an active
  // session, which could upload after withdrawal. Its container's stop(false)
  // removes listeners and discards buffered data without sending it.
  const container = (
    replay as unknown as {
      _replay?: { stop: (options: { forceFlush: boolean }) => Promise<void> };
    }
  )._replay;
  if (!container) {
    throw new Error('Sentry Replay discard is unavailable in this SDK version');
  }
  await container.stop({ forceFlush: false });
}

function warnSentryNotConfigured() {
  logger.warn(
    { event: 'sentry.init.skipped', reason: 'missing_dsn' },
    'Sentry DSN not provided for browser runtime.',
  );
}
